import { h, Component } from 'preact';
import util from 'preact-util';
import { observer } from 'mobx-preact';
import { Text } from 'preact-i18n';

@observer
class DhpHealthHistory extends Component {
    constructor(props) {
        super(props);
        this.state = {
            activeTab: 'fetch',
            period: '365',
            events: [],
            selectedIds: new Set(),
            loading: false,
            importing: false,
            error: null,
            importResult: null,
        };
    }

    componentDidMount() {
        this.loadImportedRecords();
    }

    loadImportedRecords = async () => {
        const { animal } = this.props;
        if (!animal?.id) return;
        const { animalHealthRecordStore } = this.props.stores;
        await animalHealthRecordStore.loadByAnimal(animal.id);
    }

    onPeriodChange = e => {
        this.setState({ period: e.target.value });
    }

    onTabChange = tab => {
        this.setState({ activeTab: tab });
    }

    onClickFetch = async () => {
        const { animal, producerNumber } = this.props;
        const { appState } = this.props.stores;
        const { period } = this.state;

        this.setState({ loading: true, error: null, events: [], importResult: null });

        const now = new Date();
        const fraDato = new Date(now.getTime() - (parseInt(period, 10) * 24 * 60 * 60 * 1000));
        const fraDatoStr = fraDato.toISOString().split('T')[0];

        // Construct dyreId based on species
        // Cattle (speciesId 13): markOfOrigin-earMark, others: earMark-markOfOrigin
        let dyreId = '';
        if (animal.markOfOrigin && animal.earMark) {
            const speciesId = animal.speciesId;
            if (speciesId === 13) {
                dyreId = `${animal.markOfOrigin}-${animal.earMark}`;
            } else {
                dyreId = `${animal.earMark}-${animal.markOfOrigin}`;
            }
        }

        const response = await appState.animaliaMkiPreview(`${producerNumber}`, {
            fraDato: fraDatoStr,
            dyreId,
            animalId: animal.id,
        });

        if (response?.status === 200) {
            const events = response.data || [];
            const selectedIds = new Set();
            events.forEach(ev => {
                if (!ev.alreadyImported) {
                    selectedIds.add(ev.id);
                }
            });
            this.setState({ events, selectedIds, loading: false });
        } else {
            this.setState({ loading: false, error: response?.message || 'Failed to fetch from DHP' });
        }
    }

    onToggleEvent = (eventId) => {
        const { selectedIds } = this.state;
        const newSet = new Set(selectedIds);
        if (newSet.has(eventId)) {
            newSet.delete(eventId);
        } else {
            newSet.add(eventId);
        }
        this.setState({ selectedIds: newSet });
    }

    onClickImport = async () => {
        const { animal } = this.props;
        const { appState } = this.props.stores;
        const { events, selectedIds } = this.state;

        const selectedEvents = events.filter(ev => selectedIds.has(ev.id)).map(ev => ({
            dhpEventId: ev.id,
            dhpProducerNumber: ev['produsent-nr'] || '',
            dhpVeterinaryNumber: ev['veterinaer-nr'] || '',
            dhpAnimalId: ev['dyre-id'] || '',
            dyreslag: ev.dyreslag || '',
            visitDate: ev.besoksdato,
            groupDescription: ev.flokkbeskrivelse || null,
            groupSize: ev.antall || null,
            diagnoses: (ev.diagnoser || []).map(d => ({
                code: d.diagnosekode,
                name: d.diagnosenavn,
                expiryDate: d['utgaar-dato'] || null,
            })),
            medicines: (ev.medisiner || []).map(m => ({
                medicineId: m['medisin-merke-id'],
                name: m.medisin,
                dose: m.dose,
                unit: m.enhet,
                lastTreatment: m['siste-behandling'],
                withdrawalMeat: m['tilbakeholdsfrist-kjott'],
                withdrawalMilkEgg: m['tilbakeholdsfrist-melk-egg'],
                withdrawalStartnekt: m['tilbakeholdsfrist-startnekt'],
            })),
            rawDhpData: ev,
        }));

        if (selectedEvents.length === 0) return;

        this.setState({ importing: true, error: null });

        const response = await appState.animaliaMkiImport(animal.id, selectedEvents);

        if (response?.status === 201) {
            this.setState({
                importing: false,
                importResult: response.data,
                activeTab: 'imported',
            });
            await this.loadImportedRecords();
        } else {
            this.setState({ importing: false, error: response?.message || 'Import failed' });
        }
    }

    render() {
        const { activeTab, period, events, selectedIds, loading, importing, error, importResult } = this.state;
        const { animal } = this.props;
        const { userStore, animalHealthRecordStore } = this.props.stores;
        const { user } = userStore;
        const darkmode = util.getNestedValue(user, 'settings.darkmode');
        const { animalHealthRecords = [] } = animalHealthRecordStore;

        const selectedCount = [...selectedIds].filter(id => {
            const ev = events.find(e => e.id === id);
            return ev && !ev.alreadyImported;
        }).length;

        const animalName = animal?.name || animal?.earMark || '';

        return (<>
            <div class='w-100 d-flex justify-content-center'>
                <div class='w-100 h-100' style='padding-top: 20px; padding-bottom: 100px;'>
                    <div class='w-100 d-flex flex-column'>
                        <div class='font-weight-lighter px-3 box-header d-flex flex-row justify-content-between'>
                            <Text id='dyrejournal.dhp-history-title'>DHP Health History</Text>
                            <small class='text-body-secondary'>{animalName}</small>
                        </div>

                        <div class={`d-flex flex-column mx-3 px-4 py-3 box-container ${darkmode ? 'bg-darkmode' : 'bg-lightmode'}`}>
                            {/* Tabs */}
                            <ul class='nav nav-tabs mb-3'>
                                <li class='nav-item'>
                                    <button
                                        class={`nav-link ${activeTab === 'fetch' ? 'active' : ''}`}
                                        onClick={() => this.onTabChange('fetch')}
                                    >
                                        <Text id='dyrejournal.dhp-history-fetch'>Fetch from DHP</Text>
                                    </button>
                                </li>
                                <li class='nav-item'>
                                    <button
                                        class={`nav-link ${activeTab === 'imported' ? 'active' : ''}`}
                                        onClick={() => this.onTabChange('imported')}
                                    >
                                        <Text id='dyrejournal.dhp-history-imported'>Imported Records</Text>
                                        {animalHealthRecords.length > 0 && <span class='badge bg-secondary ms-1'>{animalHealthRecords.length}</span>}
                                    </button>
                                </li>
                            </ul>

                            {/* Fetch Tab */}
                            {activeTab === 'fetch' && <>
                                <div class='d-flex flex-row align-items-center gap-2 mb-3'>
                                    <small class='text-body-secondary'><Text id='dyrejournal.dhp-history-period'>Period</Text>:</small>
                                    <select class='form-select form-select-sm' style='width: auto;' value={period} onChange={this.onPeriodChange}>
                                        <option value='90'>3 <Text id='dyrejournal.dhp-history-months'>months</Text></option>
                                        <option value='180'>6 <Text id='dyrejournal.dhp-history-months'>months</Text></option>
                                        <option value='365'>12 <Text id='dyrejournal.dhp-history-months'>months</Text></option>
                                        <option value='730'>2 <Text id='dyrejournal.dhp-history-years'>years</Text></option>
                                    </select>
                                    <button
                                        class='btn btn-sm btn-primary'
                                        onClick={this.onClickFetch}
                                        disabled={loading}
                                    >
                                        {loading ? <i class='fa-duotone fa-spinner fa-spin' /> : <i class='fa-duotone fa-download me-1' />}
                                        <Text id='dyrejournal.dhp-history-fetch-btn'>Fetch</Text>
                                    </button>
                                </div>

                                {error && <div class='alert alert-danger'>{error}</div>}

                                {importResult && <div class='alert alert-success'>
                                    <Text id='dyrejournal.dhp-history-import-success'>Imported</Text> {importResult.created?.length || 0} <Text id='dyrejournal.dhp-history-records'>records</Text>
                                    {importResult.skippedCount > 0 && <>, {importResult.skippedCount} <Text id='dyrejournal.dhp-history-skipped'>skipped (duplicates)</Text></>}
                                </div>}

                                {events.length > 0 && <>
                                    <small class='text-body-secondary mb-2'>
                                        {events.length} <Text id='dyrejournal.dhp-history-events-found'>events found</Text>
                                        {selectedCount > 0 && <> — {selectedCount} <Text id='dyrejournal.dhp-history-selected'>selected</Text></>}
                                    </small>

                                    {events.map((ev) => {
                                        const isImported = ev.alreadyImported;
                                        const isSelected = selectedIds.has(ev.id);
                                        return (
                                            <div key={ev.id} class={`border rounded p-3 mb-2 ${isImported ? 'opacity-50' : ''}`}>
                                                <div class='d-flex align-items-center gap-2 mb-1'>
                                                    <input
                                                        type='checkbox'
                                                        checked={isSelected}
                                                        disabled={isImported}
                                                        onChange={() => this.onToggleEvent(ev.id)}
                                                        style='accent-color: #2563eb;'
                                                    />
                                                    <strong>{ev.besoksdato ? ev.besoksdato.split('T')[0] : ''}</strong>
                                                    {ev['veterinaer-nr'] && <small class='text-body-secondary'>Vet: {ev['veterinaer-nr']}</small>}
                                                    {isImported && <span class='badge bg-secondary'><Text id='dyrejournal.dhp-history-already-imported'>Already imported</Text></span>}
                                                </div>
                                                <div style='margin-left: 24px; font-size: 0.85em;'>
                                                    {(ev.diagnoser || []).map(d => (
                                                        <div><span class='text-warning'><Text id='dyrejournal.dhp-history-diagnose'>Diagnose</Text>:</span> {d.diagnosenavn} ({d.diagnosekode})</div>
                                                    ))}
                                                    {(ev.medisiner || []).map(m => (
                                                        <div>
                                                            <span class='text-success'><Text id='dyrejournal.dhp-history-medicine'>Medicine</Text>:</span> {m.medisin} {m.dose}{m.enhet}
                                                            {m['tilbakeholdsfrist-kjott'] > 0 && <span class='text-body-secondary'> — <Text id='dyrejournal.dhp-history-withdrawal'>withdrawal meat</Text>: {m['tilbakeholdsfrist-kjott']}d</span>}
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        );
                                    })}

                                    {selectedCount > 0 && <div class='d-flex justify-content-end mt-2'>
                                        <button
                                            class='btn btn-success'
                                            onClick={this.onClickImport}
                                            disabled={importing}
                                        >
                                            {importing ? <i class='fa-duotone fa-spinner fa-spin me-1' /> : <i class='fa-duotone fa-file-import me-1' />}
                                            <Text id='dyrejournal.dhp-history-import-btn'>Import Selected</Text> ({selectedCount})
                                        </button>
                                    </div>}
                                </>}

                                {!loading && events.length === 0 && !error && <small class='text-body-secondary'>
                                    <Text id='dyrejournal.dhp-history-no-records'>Click Fetch to load health history from DHP</Text>
                                </small>}
                            </>}

                            {/* Imported Records Tab */}
                            {activeTab === 'imported' && <>
                                {animalHealthRecords.length === 0 && <small class='text-body-secondary'>
                                    <Text id='dyrejournal.dhp-history-no-imported'>No records imported yet</Text>
                                </small>}

                                {animalHealthRecords.map(record => (
                                    <div key={record.id} class='border rounded p-3 mb-2'>
                                        <div class='d-flex justify-content-between align-items-center mb-1'>
                                            <div>
                                                <strong>{record.visitDate ? util.formatDate(record.visitDate, { locale: 'no' }) : ''}</strong>
                                                {record.dhpVeterinaryNumber && <small class='text-body-secondary ms-2'>Vet: {record.dhpVeterinaryNumber}</small>}
                                            </div>
                                            <small class='text-body-secondary'>
                                                <Text id='dyrejournal.dhp-history-imported-on'>Imported</Text> {record.importedDate ? util.formatDate(record.importedDate, { locale: 'no' }) : ''}
                                            </small>
                                        </div>
                                        <div style='font-size: 0.85em;'>
                                            {(record.diagnoses || []).map(d => (
                                                <div><span class='text-warning'><Text id='dyrejournal.dhp-history-diagnose'>Diagnose</Text>:</span> {d.name} ({d.code})</div>
                                            ))}
                                            {(record.medicines || []).map(m => (
                                                <div>
                                                    <span class='text-success'><Text id='dyrejournal.dhp-history-medicine'>Medicine</Text>:</span> {m.name} {m.dose}{m.unit}
                                                    {m.withdrawalMeat > 0 && <span class='text-body-secondary'> — <Text id='dyrejournal.dhp-history-withdrawal'>withdrawal meat</Text>: {m.withdrawalMeat}d</span>}
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                ))}
                            </>}
                        </div>
                    </div>
                </div>
            </div>
        </>);
    }
}

export default DhpHealthHistory;
